import { EntityResponse } from "../../../../api";
import { FunctionComponent, useEffect } from "react";
import { Badge, Box, HStack, Icon, IconButton, Text, Tooltip, useDisclosure, VStack } from "@chakra-ui/react";
import { IconBuilding, IconChevronDown, IconChevronRight, IconFolder, IconPlus } from "@tabler/icons-react";
import { useEntityTreeContext } from "../EntityTreeContext";
import { sortBy } from "lodash";

export type EntityTreeItemProps = {
    entity: EntityResponse;
    filterTerm?: string;
};

function matchesFilter(entity: EntityResponse, term: string): boolean {
    if (!term) return true;
    if (entity.name.toLowerCase().includes(term.toLowerCase())) return true;
    return (entity.children ?? []).some(child => matchesFilter(child, term));
}

export const EntityTreeItem: FunctionComponent<EntityTreeItemProps> = props => {
    const { entity, filterTerm = "" } = props;
    const { onAdd, hoveringEntityId, onMouseEnter, onMouseLeave, isEditable, selectedEntity, pathToSelected, setSelectedEntityId, isExpandAll } =
        useEntityTreeContext();
    const isSelected = selectedEntity?.id === entity.id;
    const isHovering = hoveringEntityId === entity.id;

    const isDirectory = entity.entityType === "Directory",
        hasChildren = isDirectory ? entity.children.length > 0 : false;

    const isFilterActive = filterTerm.length > 0;
    const isVisible = !isFilterActive || matchesFilter(entity, filterTerm);
    const shouldForceExpand = isFilterActive && isDirectory && entity.children.some(c => matchesFilter(c, filterTerm));

    const expander = useDisclosure({ defaultIsOpen: isExpandAll || pathToSelected.includes(entity) });

    useEffect(() => {
        if (shouldForceExpand) expander.onOpen();
    }, [shouldForceExpand]);

    const isEffectivelyOpen = expander.isOpen || shouldForceExpand;
    const toggleExpansion = isDirectory ? expander.onToggle : undefined;

    const childEntities = sortBy(entity.children, i => i.name).map(child => ({
        key: child.id,
        ...props,
        entity: child,
        filterTerm,
    }));

    if (!isVisible) return null;

    return (
        <HStack align="start">
            {/* Directory chevron */}
            <Box cursor={toggleExpansion ? "pointer" : undefined} minW="2rem">
                {isDirectory && (
                    <IconButton
                        variant={"ghost"}
                        size={"xs"}
                        aria-label={`${isEffectivelyOpen ? "collapse" : "expand"} tree item`}
                        icon={<Icon as={isEffectivelyOpen ? IconChevronDown : IconChevronRight} />}
                        onClick={toggleExpansion}
                    />
                )}
            </Box>

            {/* Entity row and children */}
            <VStack align="start" spacing={0}>
                <HStack align="stretch">
                    <HStack
                        onMouseEnter={() => onMouseEnter(entity.id)}
                        onMouseLeave={() => onMouseLeave()}
                        px={".5rem"}
                        py={".375rem"}
                        pl={isSelected ? "calc(.5rem - 3px)" : ".5rem"}
                        borderLeft={isSelected ? "3px solid" : "3px solid transparent"}
                        borderColor={isSelected ? "blue.400" : "transparent"}
                        borderRadius={"sm"}
                        bg={isSelected ? "blue.50" : isHovering ? "gray.100" : "transparent"}
                        color={isSelected ? "blue.700" : "inherit"}
                        fontWeight={isSelected ? "semibold" : "normal"}
                        transition={"all var(--chakra-transition-duration-normal)"}
                        cursor={"pointer"}
                        onClick={() => setSelectedEntityId(entity.id)}
                    >
                        <Icon as={isDirectory ? IconFolder : IconBuilding} boxSize={"1rem"} />
                        <Text style={{ userSelect: "none" }}>{entity.name}</Text>
                        {!isDirectory && entity.candidateCount > 0 && (
                            <Tooltip label={`${entity.candidateCount} candidates`}>
                                <Badge colorScheme={"blue"} borderRadius={"full"}>
                                    {entity.candidateCount}
                                </Badge>
                            </Tooltip>
                        )}
                    </HStack>

                    {isEditable && isDirectory && (
                        <IconButton
                            variant={"ghost"}
                            size={"xs"}
                            aria-label="add child entity"
                            icon={<Icon as={IconPlus} />}
                            onClick={() => {
                                expander.onOpen();
                                onAdd(entity.id);
                            }}
                        />
                    )}
                </HStack>

                {hasChildren && isDirectory && isEffectivelyOpen && childEntities.map(child => <EntityTreeItem {...child} />)}
                {!hasChildren && isDirectory && isEffectivelyOpen && !isFilterActive && (
                    <Box color={"blackAlpha.500"} fontSize={"sm"} pl={".5rem"}>
                        Empty
                    </Box>
                )}
                {isDirectory && isEffectivelyOpen && <Box boxSize={".5rem"} />}
            </VStack>
        </HStack>
    );
};
